import React, { useEffect, useRef, useState } from "react";
import styles from "./LandlordsPage.module.scss";
import AboutAndContactForm from "../../components/AboutAndContactForm/AboutAndContactForm";
import { lettingProcedure, lettingVacant } from "src/assets/assetsExport";
import { NavLink, useLocation } from "react-router-dom";
import { duringLetting, preLetting } from "src/data/general";
import { backgroundImg } from "src/assets/assetsExport";
import PropertymarkBanner from "../../components/PropertymarkBanner/PropertymarkBanner";
import { submitContact } from "../../api/contact";
import { landlordCaseStudies } from "../../data/caseStudies";
import GoogleReviewsEmbed from "../../components/GoogleReviews/GoogleReviewsEmbed";
import {
  enquiryInputClassName,
  enquirySubmitClassName,
} from "../../components/forms/EnquiryFormFields";

const LANDLORD_TOOLS = [
  {
    to: "/services/rental-yield",
    number: "01",
    title: "Rental Yield",
    description: "Model gross and net yield on your investment.",
  },
  {
    to: "/services/stamp-duty-calculator",
    number: "02",
    title: "Stamp Duty",
    description: "Estimate purchase tax for buy-to-let properties.",
  },
  {
    to: "/services/rent-estimate",
    number: "03",
    title: "Rent Estimate",
    description: "Benchmark achievable rent for your address.",
  },
  {
    to: "/services/mortgage-calculator",
    number: "04",
    title: "Mortgage",
    description: "Calculate monthly repayments and affordability.",
  },
];

const THE_STANDARD_ITEMS = [
  {
    title: "Rent Guarantee Shield",
    body: "Financial protection ensuring income continuity regardless of tenant payment status.",
  },
  {
    title: "Forensic Vetting",
    body: "Bank-grade background checks including credit, employment, and Right-to-Rent verification.",
  },
  {
    title: "Legal Armor",
    body: "Watertight contracts drafted by property law specialists to protect your asset.",
  },
  {
    title: "Growth Strategy",
    body: "Quarterly reviews to identify refurbishment opportunities for capital appreciation.",
  },
];

const LandlordsPage2: React.FC = () => {
  const location = useLocation();
  const instantAppraisalRef = useRef<HTMLElement | null>(null);
  const [appraisalPostcode, setAppraisalPostcode] = useState("");
  const [appraisalBedrooms, setAppraisalBedrooms] = useState("");
  const [appraisalEmail, setAppraisalEmail] = useState("");
  const [appraisalMessage, setAppraisalMessage] = useState("");
  const [appraisalSubmitting, setAppraisalSubmitting] = useState(false);
  const [appraisalError, setAppraisalError] = useState<string | null>(null);
  const [appraisalSuccess, setAppraisalSuccess] = useState(false);

  // Smooth scroll function
  const scrollToInstantAppraisal = (e: React.MouseEvent) => {
    e.preventDefault();
    if (instantAppraisalRef.current) {
      instantAppraisalRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, [location]);

  const handleAppraisalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAppraisalError(null);
    setAppraisalSuccess(false);
    setAppraisalSubmitting(true);

    const details = [
      appraisalPostcode.trim() && `Postcode: ${appraisalPostcode.trim()}`,
      appraisalBedrooms.trim() && `Bedrooms: ${appraisalBedrooms.trim()}`,
      appraisalMessage.trim(),
    ]
      .filter(Boolean)
      .join("\n");

    const email = appraisalEmail.trim();
    const name = email.includes("@") ? email.split("@")[0] : "Property Owner";

    try {
      await submitContact({
        name,
        email,
        subject: "Instant Property Valuation",
        message: details || "Property valuation request",
        sourcePage: location.pathname,
        enquiryType: "valuation",
      });

      setAppraisalSuccess(true);
      setAppraisalPostcode("");
      setAppraisalBedrooms("");
      setAppraisalEmail("");
      setAppraisalMessage("");
    } catch (err) {
      setAppraisalError(
        err instanceof Error ? err.message : "Failed to send your enquiry"
      );
    } finally {
      setAppraisalSubmitting(false);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="min-h-[90vh] pt-10 pb-20 w-full relative flex items-center justify-center">
        <img
          src={backgroundImg.livingRoom5}
          alt="Landlord hero"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
        />
        <div className="absolute inset-0 bg-black/30 z-0"></div>
        <div className="relative z-10 text-center max-w-4xl px-6">
          <h1 className="font-teko leading-none mb-8 tracking-[-2px] text-6xl">
            <span className="stroke-1 text-white">Maximize Asset Yield</span>
          </h1>
          <p className="font-manrope text-base leading-relaxed text-white max-w-2xl mx-auto mb-10">
            The operating system for high-performance property portfolios.
            Precision valuation, automated compliance, and complete transparency
            for London landlords.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6">
            <button
              className="px-6 py-2 bg-text-obsidian text-white font-teko text-sm uppercase tracking-wider border border-white/10 transform skew-x-[var(--skew)] transition-all duration-300 hover:bg-brand-yellow hover:text-black"
              onClick={scrollToInstantAppraisal}
            >
              <span className="block transform skew-x-[10deg]">
                Get Valuation
              </span>
            </button>
            <NavLink
              to="/services/rent-estimate"
              className="px-6 py-2 bg-brand-yellow text-text-obsidian font-teko text-sm uppercase tracking-wider transform skew-x-[var(--skew)] transition-all duration-300 hover:bg-yellow-400 hover:text-black"
              target="_blank"
            >
              <span className="block transform skew-x-[10deg]">
                Instant Rent Estimate
              </span>
            </NavLink>
            <NavLink
              to="/contact-us"
              className="px-6 py-2 bg-white/90 text-text-obsidian font-teko text-sm uppercase tracking-wider transform skew-x-[var(--skew)] transition-all duration-300 hover:bg-white"
              target="_blank"
            >
              <span className="block transform skew-x-[10deg]">
                Book Strategy Call
              </span>
            </NavLink>
          </div>
        </div>
      </section>

      <PropertymarkBanner />

      {/* --- Instant Appraisal MINI SECTION --- */}
      <section
        ref={instantAppraisalRef}
        className="relative bg-[linear-gradient(120deg,#f7f7f9_60%,#fef9ef_100%)] py-24 border-t border-t-solid border-t-[rgba(0,0,0,0.04)]"
      >
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-10 px-[5%] items-start">
          <div className="mt-12">
            <h2 className="page-section-heading mb-4">
              Submit Your Property Details
            </h2>
            <p className="font-manrope text-base text-text-slate mb-8 max-w-[400px] leading-relaxed">
              Fill out the form to request an accurate rental or sales valuation
              from our team. Enter your property's details and we'll be in touch
              with a tailored assessment.
            </p>
            <ul className="list-none space-y-2 mb-4">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-brand-yellow rounded-full inline-block"></span>
                <span className="font-manrope text-sm text-text-slate">
                  Professional, data-driven advice
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-brand-yellow rounded-full inline-block"></span>
                <span className="font-manrope text-sm text-text-slate">
                  All London locations considered
                </span>
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-brand-yellow rounded-full inline-block"></span>
                <span className="font-manrope text-sm text-text-slate">
                  Simple, no commitment enquiry
                </span>
              </li>
            </ul>
          </div>
          {/* Reworked Floating Widget - more elegant, light glass effect */}
          <div className="flex-1">
            <div className="bg-bg-white p-6 border border-solid border-[rgba(0,0,0,0.06)] border-l-4 border-l-brand-yellow">
              <h3 className="font-teko text-xl mb-4">Property Enquiry</h3>
              <form className="grid gap-3" onSubmit={handleAppraisalSubmit}>
                {appraisalSuccess && (
                  <p className="font-manrope text-sm text-green-700 bg-green-50 p-3 border border-solid border-green-200">
                    Thank you — your valuation request has been sent.
                  </p>
                )}
                {appraisalError && (
                  <p className="font-manrope text-sm text-red-700 bg-red-50 p-3 border border-solid border-red-200">
                    {appraisalError}
                  </p>
                )}
                <input
                  type="text"
                  className={enquiryInputClassName}
                  placeholder="Postcode (e.g. SW1A 1AA)"
                  id="postcodeInput"
                  value={appraisalPostcode}
                  onChange={(e) => setAppraisalPostcode(e.target.value)}
                  required
                  disabled={appraisalSubmitting}
                />
                <input
                  type="number"
                  min="0"
                  className={enquiryInputClassName}
                  placeholder="Bedrooms (e.g. 2)"
                  id="bedroomsInput"
                  value={appraisalBedrooms}
                  onChange={(e) => setAppraisalBedrooms(e.target.value)}
                  required
                  disabled={appraisalSubmitting}
                />
                <input
                  type="email"
                  className={enquiryInputClassName}
                  placeholder="Your Email"
                  id="emailInput"
                  value={appraisalEmail}
                  onChange={(e) => setAppraisalEmail(e.target.value)}
                  required
                  disabled={appraisalSubmitting}
                />
                <textarea
                  className={enquiryInputClassName}
                  rows={3}
                  placeholder="Additional Message / Requirements"
                  id="messageInput"
                  value={appraisalMessage}
                  onChange={(e) => setAppraisalMessage(e.target.value)}
                  disabled={appraisalSubmitting}
                />
                <button
                  type="submit"
                  className={enquirySubmitClassName}
                  disabled={appraisalSubmitting}
                >
                  {appraisalSubmitting ? "Sending…" : "Analyze Property"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* 1. PROCESS (Horizontal Timeline) */}
      <section
        id="pre-letting"
        className="py-40 relative bg-bg-white scroll-mt-28"
      >
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-24 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              Find a good tenant
            </span>
            <h2 className="page-section-heading">
              Pre-Letting & Compliance
            </h2>
          </div>

          <div
            className={`
              ${styles["timeline-track"]}
              grid
              grid-cols-1
              sm:grid-cols-2
              lg:grid-cols-3
              xl:grid-cols-4
              gap-12
              justify-between
              relative
              mt-16
            `}
          >
            {preLetting.map((i: any, index: number) => {
              return (
                <div className="flex-1 relative transition-all duration-400 hover:translate-y-[-10px] group md:pt-0 pr-0 md:pr-8 pl-8 md:pl-0">
                  {/* Vertical/Horizontal timeline line */}
                  <span
                    className={`
                      absolute
                      ${/* vertical on mobile, horizontal on md+ */ ""}
                      left-0
                      top-0
                      h-full
                      w-px
                      bg-[rgba(0,0,0,0.1)]
                      md:h-px
                      md:w-full
                      md:top-1
                      md:left-0
                      z-0
                    `}
                    aria-hidden="true"
                  ></span>
                  {/* Step marker */}
                  <span className="absolute top-[-4px] left-0 w-2.5 h-2.5 bg-bg-white border border-solid border-text-obsidian transform rotate-45 transition-all duration-400 group-hover:bg-brand-yellow group-hover:border-brand-yellow group-hover:scale-150 z-10"></span>
                  <span className="font-teko text-lg text-brand-gold mb-2 block md:mt-10 relative z-10">
                    0{index + 1}
                  </span>
                  <h3 className="font-teko text-2xl mb-4 relative z-10">
                    {i.header}
                  </h3>
                  <p className="font-manrope text-sm text-text-slate leading-relaxed relative z-10">
                    {i.p}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <section
        id="during-tenancy"
        className="py-40 relative bg-bg-white scroll-mt-28"
      >
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-24 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              We take care of everything
            </span>
            <h2 className="page-section-heading">
              During Tenancy
            </h2>
          </div>

          <div
            className={`
              ${styles["timeline-track"]}
              grid
              grid-cols-1
              sm:grid-cols-2
              lg:grid-cols-3
              xl:grid-cols-4
              gap-12
              justify-between
              relative
              mt-16
            `}
          >
            {duringLetting.map((i: any, index: number) => {
              return (
                <div className="flex-1 relative transition-all duration-400 hover:translate-y-[-10px] group md:pt-0 pr-0 md:pr-8 pl-8 md:pl-0">
                  {/* Vertical/Horizontal timeline line */}
                  <span
                    className={`
                      absolute
                      ${/* vertical on mobile, horizontal on md+ */ ""}
                      left-0
                      top-0
                      h-full
                      w-px
                      bg-[rgba(0,0,0,0.1)]
                      md:h-px
                      md:w-full
                      md:top-1
                      md:left-0
                      z-0
                    `}
                    aria-hidden="true"
                  ></span>
                  {/* Step marker */}
                  <span className="absolute top-[-4px] left-0 w-2.5 h-2.5 bg-bg-white border border-solid border-text-obsidian transform rotate-45 transition-all duration-400 group-hover:bg-brand-yellow group-hover:border-brand-yellow group-hover:scale-150 z-10"></span>
                  <span className="font-teko text-lg text-brand-gold mb-2 block md:mt-10 relative z-10">
                    0{index + 1}
                  </span>
                  <h3 className="font-teko text-2xl mb-4 relative z-10">
                    {i.header}
                  </h3>
                  <p className="font-manrope text-sm text-text-slate leading-relaxed relative z-10">
                    {i.p}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section
        id="end-of-tenancy"
        className="py-40 relative bg-bg-porcelain scroll-mt-28"
      >
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-16 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              Check-out
            </span>
            <h2 className="page-section-heading">
              End of Tenancy
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
            {[
              {
                title: "Notice & renewal",
                body: "We manage notice periods and renewal discussions in line with your tenancy agreement, keeping you informed of tenant intentions well ahead of expiry.",
              },
              {
                title: "Check-out inventory",
                body: "A detailed check-out report is prepared against the check-in inventory, documenting the property condition and any matters requiring attention.",
              },
              {
                title: "Deposit reconciliation",
                body: "We negotiate deposit deductions with tenants where appropriate and facilitate return via the Deposit Protection Service, minimising disputes.",
              },
              {
                title: "Re-marketing",
                body: "Once vacant, we prepare the property for re-letting — coordinating cleaning, minor works, and photography to minimise void periods.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)]"
              >
                <h3 className="font-teko text-2xl mb-3">{item.title}</h3>
                <p className="font-manrope text-sm text-text-slate leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 2. TRUST US (Minimal Grid) */}
      <section className="py-32 relative bg-bg-white">
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-20 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              Advantages
            </span>
            <h2 className="page-section-heading">
              The Standard
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {THE_STANDARD_ITEMS.map((item, index) => (
              <div
                key={item.title}
                className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)] transition-all duration-300 ease-lux cursor-default relative hover:bg-text-obsidian hover:text-white hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(0,0,0,0.12)] group"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-brand-yellow transition-all duration-300 group-hover:bg-white" />
                <span className="font-teko text-4xl text-brand-yellow/50 leading-none mb-3 block group-hover:text-white/30">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="font-teko text-xl mb-3">{item.title}</h3>
                <p className="font-manrope text-sm text-text-slate leading-relaxed group-hover:text-white">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-40 relative bg-bg-white">
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-24 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              Results
            </span>
            <h2 className="page-section-heading">
              Landlord Case Studies
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {landlordCaseStudies.map((study) => (
              <article
                key={study.id}
                className="bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.05)] p-8 flex flex-col transition-all duration-300 hover:shadow-soft hover:-translate-y-1"
              >
                <span className="font-manrope text-xs uppercase tracking-wider text-brand-gold mb-2">
                  {study.location} · {study.propertyType}
                </span>
                <h3 className="font-teko text-2xl text-text-obsidian mb-4 leading-tight">
                  {study.title}
                </h3>
                <p className="font-manrope text-sm text-text-slate leading-relaxed mb-4">
                  <span className="font-semibold text-text-obsidian">
                    Challenge:{" "}
                  </span>
                  {study.challenge}
                </p>
                <p className="font-manrope text-sm text-text-slate leading-relaxed mb-4">
                  <span className="font-semibold text-text-obsidian">
                    Outcome:{" "}
                  </span>
                  {study.outcome}
                </p>
                {study.metric ? (
                  <p className="font-teko text-lg text-brand-yellow mt-auto pt-4 border-t border-t-solid border-t-[rgba(0,0,0,0.06)]">
                    {study.metric}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 2. PRICE LISTS */}
      <section className="py-40 relative bg-[rgba(249,249,249,0.75)]">
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-24 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              Documents
            </span>
            <h2 className="page-section-heading">
              Price Lists
            </h2>
          </div>
          <div
            className={`${
              styles["guide-scroller"] || ""
            } flex gap-12 pb-8 overflow-x-auto`}
          >
            <div className="min-w-[350px] bg-white border border-solid border-[#eee] p-12 px-8 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft group">
              <div className="absolute top-0 left-0 w-full h-1 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
                Lettings
              </span>
              <h3 className="font-teko text-xl mb-6 leading-tight">
                Letting procedure and price list {new Date().getFullYear()}
              </h3>
              <a
                href={lettingProcedure}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block font-teko text-sm uppercase px-4 py-2 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.1)] text-text-obsidian group-hover:bg-brand-yellow group-hover:border-brand-yellow transition-all duration-300"
              >
                Download PDF
              </a>
            </div>
            <div className="min-w-[350px] bg-white border border-solid border-[#eee] p-12 px-8 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft group">
              <div className="absolute top-0 left-0 w-full h-1 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
                Management
              </span>
              <h3 className="font-teko text-xl mb-6 leading-tight">
                Vacant property management price list {new Date().getFullYear()}
              </h3>
              <a
                href={lettingVacant}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block font-teko text-sm uppercase px-4 py-2 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.1)] text-text-obsidian group-hover:bg-brand-yellow group-hover:border-brand-yellow transition-all duration-300"
              >
                Download PDF
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CALCULATORS (App Dock) */}
      <section className="py-40 relative bg-[#1a1a1a] text-white">
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-24 relative pl-8 border-l-2 border-l-solid border-l-[rgba(255,255,255,0.2)]">
            <span className="font-manrope text-xs tracking-wider uppercase text-[rgba(255,255,255,0.5)] block mb-2">
              Tools
            </span>
            <h2 className="page-section-heading text-white">
              Landlord Tools & Calculators
            </h2>
            <p className="font-manrope text-sm text-white/70 mt-3 max-w-xl">
              Quick calculators to model yield, tax, rent, and mortgage costs
              before you instruct us.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {LANDLORD_TOOLS.map((tool) => (
              <NavLink
                key={tool.to}
                to={tool.to}
                className="bg-[rgba(255,255,255,0.05)] border border-solid border-[rgba(255,255,255,0.1)] p-6 flex flex-col gap-2 transition-all duration-300 hover:border-brand-yellow hover:-translate-y-1 group no-underline text-white"
              >
                <span className="font-teko text-3xl text-brand-yellow/40 group-hover:text-brand-yellow transition-colors">
                  {tool.number}
                </span>
                <span className="font-teko text-lg uppercase tracking-wide group-hover:text-brand-yellow transition-colors">
                  {tool.title}
                </span>
                <p className="font-manrope text-xs text-white/60 leading-relaxed flex-1">
                  {tool.description}
                </p>
                <span className="font-teko text-xs uppercase text-brand-yellow opacity-0 group-hover:opacity-100 transition-opacity">
                  Open →
                </span>
              </NavLink>
            ))}
          </div>
        </div>
      </section>

      {/* 4. GUIDES (Dossiers) */}
      <section className="py-40 relative">
        <div className="max-w-[1440px] mx-auto px-[5%]">
          <div className="mb-24 relative pl-8 border-l-2 border-l-solid border-l-brand-yellow">
            <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
              Intelligence
            </span>
            <h2 className="page-section-heading">
              Market Briefings
            </h2>
          </div>
          <div
            className={`${styles["guide-scroller"]} flex gap-12 pb-8 overflow-x-auto`}
          >
            <div className="min-w-[350px] bg-white border border-solid border-[#eee] p-12 px-8 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft group">
              <div className="absolute top-0 left-0 w-full h-1 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
                Forecast
              </span>
              <h3 className="font-teko text-xl mb-6 leading-tight">
                {new Date().getFullYear()} Market Outlook
              </h3>
              <NavLink
                to="/guides/landlords#pre-letting"
                className="inline-block font-teko text-sm uppercase px-4 py-2 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.1)] text-text-obsidian group-hover:bg-brand-yellow group-hover:border-brand-yellow transition-all duration-300"
              >
                View guide
              </NavLink>
            </div>
            <div className="min-w-[350px] bg-white border border-solid border-[#eee] p-12 px-8 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft group">
              <div className="absolute top-0 left-0 w-full h-1 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
                Compliance
              </span>
              <h3 className="font-teko text-xl mb-6 leading-tight">
                Landlord Safety Checklist
              </h3>
              <NavLink
                to="/guides/landlords#pre-letting"
                className="inline-block font-teko text-sm uppercase px-4 py-2 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.1)] text-text-obsidian group-hover:bg-brand-yellow group-hover:border-brand-yellow transition-all duration-300"
              >
                View checklist
              </NavLink>
            </div>
            <div className="min-w-[350px] bg-white border border-solid border-[#eee] p-12 px-8 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft group">
              <div className="absolute top-0 left-0 w-full h-1 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
                Finance
              </span>
              <h3 className="font-teko text-xl mb-6 leading-tight">
                Tax Efficiency Guide
              </h3>
              <NavLink
                to="/guides/landlords#during-tenancy"
                className="inline-block font-teko text-sm uppercase px-4 py-2 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.1)] text-text-obsidian group-hover:bg-brand-yellow group-hover:border-brand-yellow transition-all duration-300"
              >
                View guide
              </NavLink>
            </div>
            <div className="min-w-[350px] bg-white border border-solid border-[#eee] p-12 px-8 relative transition-all duration-400 cursor-pointer hover:translate-y-[-10px] hover:shadow-soft group">
              <div className="absolute top-0 left-0 w-full h-1 bg-text-obsidian transition-all duration-400 group-hover:bg-brand-yellow"></div>
              <span className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-4 block">
                Strategy
              </span>
              <h3 className="font-teko text-xl mb-6 leading-tight">
                Overseas Investment
              </h3>
              <NavLink
                to="/guides/landlords#end-of-tenancy"
                className="inline-block font-teko text-sm uppercase px-4 py-2 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.1)] text-text-obsidian group-hover:bg-brand-yellow group-hover:border-brand-yellow transition-all duration-300"
              >
                View guide
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      <GoogleReviewsEmbed variant="dark" />
      <AboutAndContactForm />
    </>
  );
};

export default LandlordsPage2;
